import { useState, useRef, useEffect, useMemo } from 'react';
import { RoutePerson } from '../../@types/routes';
import { User, UsersFilterParams } from '../../@types/users';
import { useUsers } from '../../queries/users';
import { getPersonName } from '../../utils/routes';
import { CloseIcon } from '../../assets/svgs';
import { Input } from '../input';
import {
  AddNewItem,
  Chip,
  ChipRemove,
  ChipsContainer,
  ChipText,
  Container,
  Dropdown,
  DropdownItem,
  EmptyMessage,
  LabelText,
} from './style';

export type PeopleSelectorProps = {
  label?: string;
  placeholder?: string;
  mode?: 'single' | 'multiple';
  selected?: RoutePerson[];
  onSelectItem: (user: User) => void;
  onRemoveItem?: (person: RoutePerson, index: number) => void;
  onAddNewRequest?: (typedQuery: string) => void;
  backendFilters?: UsersFilterParams;
  clientFilterFn?: (user: User) => boolean;
  disabled?: boolean;
};

export const PeopleSelector = ({
  label,
  placeholder = 'חיפוש...',
  mode = 'single',
  selected = [],
  onSelectItem,
  onRemoveItem,
  onAddNewRequest,
  backendFilters,
  clientFilterFn,
  disabled = false,
}: PeopleSelectorProps) => {
  const [inputValue, setInputValue] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: users = [], isLoading } = useUsers(backendFilters);

  const shouldRenderInput = mode === 'multiple' || selected.length === 0;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredUsers = useMemo(() => {
    let result = users;

    if (selected.length > 0) {
      result = result.filter((u) => {
        const userFullName = `${u.firstName} ${u.lastName}`.trim().toLowerCase();

        return !selected.some((s) => {
          if (s.id && s.id === u.id) {
            return true;
          }

          return getPersonName(s).toLowerCase() === userFullName;
        });
      });
    }

    if (clientFilterFn) {
      result = result.filter(clientFilterFn);
    }

    const trimmedQuery = inputValue.trim().toLowerCase();
    if (trimmedQuery) {
      result = result.filter((u) => {
        const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
        const matchFull = fullName.includes(trimmedQuery);
        const matchFirst = u.firstName.toLowerCase().includes(trimmedQuery);
        const matchLast = u.lastName.toLowerCase().includes(trimmedQuery);

        return matchFull || matchFirst || matchLast;
      });
    }

    return result;
  }, [users, selected, clientFilterFn, inputValue]);

  const handleSelect = (user: User) => {
    onSelectItem(user);
    setInputValue('');
    if (mode === 'single') {
      setShowOptions(false);
    }
  };

  const handleAddNew = () => {
    if (onAddNewRequest) {
      onAddNewRequest(inputValue.trim());
      setInputValue('');
      setShowOptions(false);
    }
  };

  return (
    <Container ref={containerRef}>
      {label && <LabelText>{label}</LabelText>}

      {/* Selected Chips */}
      {selected.length > 0 && (
        <ChipsContainer>
          {selected.map((item, index) => {
            const personName = getPersonName(item);

            return (
              <Chip key={`${item.id || personName}-${index}`}>
                <ChipText>{personName}</ChipText>
                {!disabled && onRemoveItem && (
                  <ChipRemove
                    type="button"
                    onClick={() => onRemoveItem(item, index)}
                    title="הסרת משתמש"
                  >
                    <CloseIcon width={16} height={16} />
                  </ChipRemove>
                )}
              </Chip>
            );
          })}
        </ChipsContainer>
      )}

      {/* Input & Dropdown */}
      {shouldRenderInput && (
        <>
          <Input
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowOptions(true);
            }}
            onFocus={() => setShowOptions(true)}
          />
          {showOptions && !disabled && (
            <Dropdown>
              {isLoading && <EmptyMessage>טוען משתמשים...</EmptyMessage>}

              {!isLoading &&
                filteredUsers.length > 0 &&
                filteredUsers.map((user) => (
                  <DropdownItem
                    key={user.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(user)}
                  >
                    {user.firstName} {user.lastName}
                  </DropdownItem>
                ))}

              {!isLoading && filteredUsers.length === 0 && (
                <EmptyMessage>לא נמצאו משתמשים</EmptyMessage>
              )}

              {onAddNewRequest && (
                <AddNewItem onMouseDown={(e) => e.preventDefault()} onClick={handleAddNew}>
                  + הוספת משתמש חדש{inputValue.trim() ? `: "${inputValue.trim()}"` : ''}
                </AddNewItem>
              )}
            </Dropdown>
          )}
        </>
      )}
    </Container>
  );
};
